/**
 * BLU-660 — client order-status dashboard chip styling.
 *
 * The 5-status ladder (New order -> Picking -> Printed -> Ready to pack ->
 * Shipped) reuses the exact colour progression the Purchase Orders list already
 * ships (features/purchase-orders/utils/status-style.ts: slate -> blue ->
 * violet -> indigo -> emerald) so status reads identically across inventory-app
 * surfaces. Each colour still means one thing per DESIGN.md §1 — emerald is the
 * reserved "done / completed" token (here: Shipped), and amber is deliberately
 * NOT used (it is reserved for editing / warning / attention, and none of these
 * five ladder steps is a warning).
 *
 * BLU-834: `cancelled` is the terminal off-ladder state: no live FO remains
 * and at least one CANCELLED FO carries the genuine Shopify/customer
 * `cancelled_at` stamp. It uses red — the PO list's own cancelled token
 * (features/purchase-orders/utils/status-style.ts) — so the two ladders stay
 * aligned on what red means: cancelled / terminal.
 *
 * Colour is never the sole signal: every chip renders its text label alongside
 * the fill (DESIGN.md §16 colour-as-only-information ban). The visible labels
 * are sentence-case (DESIGN.md §17) and live in the `clientOrders` i18n
 * namespace (en/zh) — this map only resolves each status to its translation
 * key so status-style stays the single source of the status -> (colour, key)
 * mapping.
 */
import type { ClientOrderStatus } from "@threadheads/shared";

/** Maps each status to its `clientOrders` i18n key. Callers resolve the visible
 *  label via `t(CLIENT_ORDER_STATUS_LABEL_KEY[status])`. */
export const CLIENT_ORDER_STATUS_LABEL_KEY: Record<ClientOrderStatus, string> = {
  new_order: "status.new_order",
  picking: "status.picking",
  printed: "status.printed",
  ready_to_pack: "status.ready_to_pack",
  shipped: "status.shipped",
  cancelled: "status.cancelled",
};

/** Chip background + text colour per status. Mirrors STATUS_SELECT_CLASS from
 *  the PO list so the two ladders stay visually in sync. */
export const CLIENT_ORDER_STATUS_CHIP_CLASS: Record<ClientOrderStatus, string> = {
  new_order: "bg-slate-100 text-slate-700",
  picking: "bg-blue-100 text-blue-800",
  printed: "bg-violet-100 text-violet-800",
  ready_to_pack: "bg-indigo-100 text-indigo-800",
  shipped: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};
